import { useLocation } from "react-router";
import { useCallback, useEffect, useRef } from "react";
import { trackClick, trackPageSummary, trackPageView } from "~/lib/fpd/tracker.client";

function getScrollPercent(): number {
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
    return Math.min((scrollTop / (scrollHeight - clientHeight)) * 100, 100);
}

export default function Tracker() {
    const location = useLocation();
    const pageStartTimeRef = useRef<number>(undefined);
    const minScrollRef = useRef(0);
    const maxScrollRef = useRef(0);

    /**
     * Tracks the min and max scroll percentage.
     */
    useEffect(() => {
        const handleScroll = () => {
            const currentPercent = getScrollPercent();
            if (currentPercent < minScrollRef.current) {
                minScrollRef.current = currentPercent;
            } else if (currentPercent > maxScrollRef.current) {
                maxScrollRef.current = currentPercent;
            }
        };
        document.addEventListener("scroll", handleScroll);
        return () => {
            document.removeEventListener("scroll", handleScroll);
        };
    }, []);

    /**
     * Calculates time on page and sends page_summary event.
     */
    const sendPageSummary = useCallback(() => {
        if (pageStartTimeRef.current === undefined) {
            return;
        }
        const now = new Date().getTime();
        const timeOnPageMs = now - pageStartTimeRef.current;
        if (timeOnPageMs < 2) {
            // In strict mode, the re-triggering of effects creates a page summary on the initial view
            // with an extremely short duration -- just ignoring those.
            return;
        }
        trackPageSummary(timeOnPageMs, minScrollRef.current, maxScrollRef.current);
        const current = getScrollPercent();
        minScrollRef.current = current;
        maxScrollRef.current = current;
    }, [pageStartTimeRef, maxScrollRef, minScrollRef]);

    /**
     * On pathname change, submits a page_view event.
     * If this is not the initial page load, also submits page_summary event.
     */
    useEffect(() => {
        if (pageStartTimeRef.current !== undefined) {
            sendPageSummary();
        }
        trackPageView();
        pageStartTimeRef.current = new Date().getTime();
    }, [location.pathname, sendPageSummary]);

    /**
     * On clicks, submits a click event
     */
    useEffect(() => {
        const handleClick = (event: MouseEvent) => {
            const clickedElement = event.target as HTMLElement;
            const id = clickedElement.id;
            const tag = clickedElement.tagName;
            const text = clickedElement.textContent?.trim() || "No text for target";
            trackClick(id, tag, text);
        };
        document.addEventListener("click", handleClick);
        return () => {
            document.removeEventListener("click", handleClick);
        };
    }, []);

    /**
     * When the user closes or switches tabs, records a page summary event.
     * When the user returns, starts a new page summary session.
     */
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === "hidden") {
                sendPageSummary();
            } else {
                pageStartTimeRef.current = new Date().getTime();
            }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [sendPageSummary, pageStartTimeRef]);

    return <></>;
}
